import React, { useMemo, useState, useEffect } from 'react'
import { Center, Box, Text, Spinner } from '@chakra-ui/react'
import Header from '../../components/Header'
import CohortForm from './Forms/CohortForm'
import { useNavigate } from 'react-router-dom'
import API from '../../helpers/axiosInstance'
import { getApiHeaders, hasPermission } from '../../utils/DefaultFunction'

// CohortCondition's Time Frame Select supports exactly these values; anything
// else renders blank in Chakra UI. Map the agent's broader vocabulary onto
// the closest supported option so the dropdown picks up the prefilled value.
const SUPPORTED_TIMEFRAMES = new Set([
    'all_time', 'last_24_hours', 'last_7_days', 'last_30_days', 'last_90_days', 'last_year', 'custom_range'
]);
const TIMEFRAME_REMAP = {
    realtime: 'last_24_hours',
    last_hour: 'last_24_hours',
    last_24h: 'last_24_hours',
    last_14_days: 'last_30_days',   // closest available
    last_60_days: 'last_90_days',   // closest available
    last_180_days: 'last_year',
    last_365_days: 'last_year'
};

function normalizeTimeFrame(tf) {
    if (!tf) return 'all_time';
    if (SUPPORTED_TIMEFRAMES.has(tf)) return tf;
    return TIMEFRAME_REMAP[tf] || 'all_time';
}

const AddCohorts = ({ header }) => {
    const navigate = useNavigate()

    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const permParams = "marketing/cohorts";

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const xPermission = hasPermission("cohort-create", permissions);

    // LOCAL ONLY — AI Segment Suggestion prefill.
    // When the operator clicks "Use this segment" on an AI suggestion (either
    // inside the Predictive Segmentation modal OR the AI Suggestions Accordion
    // on the Cohorts list page) we stash the canonical condition shape in
    // sessionStorage and navigate here. CohortForm already accepts an
    // `editingCohort` prop with `{ name, description, conditions }`; we just
    // synthesize that, mapping the agent's `timeframe` → form's `time_frame`
    // and normalising the timeframe vocabulary onto what the dropdown shows.
    const prefill = useMemo(() => {
        try {
            const raw = sessionStorage.getItem("ai-cohort-prefill");
            if (!raw) return null;
            const p = JSON.parse(raw);
            if (!p) return null;
            const conditions = (p.canonical || []).map((c, i) => {
                const rawTf = c.time_frame || c.timeframe || 'all_time';
                return {
                    ...c,
                    id: i + 1,
                    type: c.type || 'event',
                    event: c.event || '',
                    property: c.property || '',
                    operator: c.operator || 'equals',
                    value: c.value ?? '',
                    time_frame: normalizeTimeFrame(rawTf)
                };
            });
            // One-shot: consume the prefill so a manual reload won't re-apply it.
            sessionStorage.removeItem("ai-cohort-prefill");
            return {
                name: p.name || '',
                description: p.description || '',
                refresh_frequency: p.refresh_frequency || 'daily',
                conditions
            };
        } catch { return null; }
    }, []);

    const handleSaveSuccess = (updatedCohortData) => {
        navigate(-1);
    };

    if (permissionsLoading) {
        return (
            <>
                <Header header={header} />
                <Center h="400px"><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
            </>
        );
    }
    if (!xPermission) {
        return (
            <>
                <Header header={header} />
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                        <Text fontSize="sm" color="gray.500">You don't have permission to view this sections.</Text>
                    </Box>
                </Center>
            </>
        );
    }

    return (
        <>
            <Header header={header} />
            <CohortForm onSave={handleSaveSuccess} editingCohort={prefill} />
        </>
    )
}

export default AddCohorts
